import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { marker } from '@common/translate-marker';

const COPIED_STATE_DURATION_MS = 2000;

/**
 * The two small buttons shown beside a collapsible textarea whose text does not fit on one line:
 * one copies the complete value, the other asks the parent to expand or collapse the field.
 * Both keep focus where it is on mouse press (see the template) so that pressing them never
 * blurs the textarea and triggers its auto-collapse before the click lands.
 */
@Component({
    selector: 'app-textarea-actions',
    templateUrl: './textarea-actions.component.html',
    styleUrls: ['./textarea-actions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TextareaActionsComponent implements OnDestroy {
    @Input() value: string | null | undefined;
    @Input() expanded = false;

    @Output() toggleExpanded = new EventEmitter<void>();

    isCopied = false;

    private readonly copyLabel = marker('Copy to clipboard');
    private readonly copiedLabel = marker('Copied to clipboard');
    private readonly expandLabel = marker('Show full value');
    private readonly collapseLabel = marker('Collapse');

    // Typed `any`: this project's global `setTimeout`/`clearTimeout` resolve ambiguously between
    // the DOM (`number`) and @types/node (`NodeJS.Timeout`) overloads depending on build context.
    private copiedTimeout?: any;

    constructor(private clipboard: Clipboard) {}

    get copyAriaLabel(): string {
        return this.isCopied ? this.copiedLabel : this.copyLabel;
    }

    get toggleAriaLabel(): string {
        return this.expanded ? this.collapseLabel : this.expandLabel;
    }

    ngOnDestroy(): void {
        clearTimeout(this.copiedTimeout);
    }

    onCopy(event: Event): void {
        event.stopPropagation();

        if (!this.value) {
            return;
        }

        this.clipboard.copy(this.value);
        this.isCopied = true;
        clearTimeout(this.copiedTimeout);
        this.copiedTimeout = setTimeout(() => {
            this.isCopied = false;
        }, COPIED_STATE_DURATION_MS);
    }

    onToggle(event: Event): void {
        event.stopPropagation();
        this.toggleExpanded.emit();
    }

    keepFocus(event: Event): void {
        event.preventDefault();
    }
}
